"use client";

import { c as _c } from "react/compiler-runtime";
import { TaskActivityTimeline } from "../components/task-activity-timeline";
import { AssigneeBadge, LocationBadge, PatientBadge, PriorityBadge, StatusBadge, SubscribersBadge, TeamBadge } from "../components/task-inline-editors";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { RiCalendarCheckLine, RiCalendarLine, RiCheckLine, RiCloseLine, RiDeleteBinLine, RiExternalLinkLine, RiFileCopyLine, RiFileList2Line, RiFlagLine, RiHeartPulseLine, RiMapPinLine, RiMore2Fill, RiNotificationLine, RiNotificationOffLine, RiPhoneLine, RiPrinterLine, RiRadioButtonLine, RiSendPlane2Line, RiTeamLine, RiTimeLine, RiUser3Line, RiUserHeartLine, RiUserLine } from "react-icons/ri";
import { CarePlanBadge } from "@/components/care-plan/care-plan-badge";
import { ReadOnlyCarePlan } from "@/components/care-plan/read-only-care-plan";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import { NotificationReferenceContent } from "@/components/notifications/notification-reference-content";
import { ProgressBarLink } from "@/components/progress-bar";
import { ComposerAttachButton, ComposerFilePreviews, useComposerFiles } from "@/components/team-chat/composer-files";
import { TaskMentionEditor } from "@/components/tiptap/task-mention-editor";
import Button from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollShadow } from "@/components/ui/scroll-shadow";
import { Skeleton } from "@/components/ui/skeleton";
import { SmartDateInput } from "@/components/ui/smart-date-input";
import { useUser } from "@/contexts/user-provider";
import { useCanAssignTasks, useCanWriteTasks } from "@/hooks/use-can-write-tasks";
import { useLocations } from "@/hooks/use-locations";
import { dayMarker, pickedDay } from "@/lib/clinic-clock";
import { calculateRelativeDuration, formatDateTime, formatStaffName } from "@/lib/format-data";
import { parseNotificationContent } from "@/lib/notification-reference";
import { parseError } from "@/lib/parse-error";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { PatientSummary } from "@/types/patient";
import { TaskCreate, TaskStatus, TaskUpdate } from "@/types/task";
import { carePlanQueryOptions } from "@/api/provider/patient/care-plan-query";
import { useTask, useTaskActivity, useTaskMutations } from "@/api/provider/tasks/tasks-query";
interface TaskDetailModalProps {
  taskId: string;
  isOpen: boolean;
  onClose: () => void;
}
interface TaskDetailPanelContentProps {
  taskId: string;
  onClose: () => void;
  className?: string;
  showCloseButton?: boolean;
}
function preventTaskDetailInteractOutside(e: {
  target: EventTarget | null;
  preventDefault: () => void;
}) {
  const target = e.target as HTMLElement | null;
  if (target?.closest("[role=listbox], [role=menu], [role=dialog], [role=alertdialog], [data-radix-popper-content-wrapper]")) {
    e.preventDefault();
  }
}
function PropertyRow(t0) {
  const $ = _c(12);
  const {
    icon: Icon,
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = <Icon className="size-3.5" />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="text-caption">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center gap-1.5 w-28 shrink-0 text-foreground-muted">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children) {
    t4 = <div className="flex-1 min-w-0">{children}</div>;
    $[7] = children;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div className="flex items-center min-h-[32px] -mx-1 px-1 rounded">{t3}{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
const TASK_DETAIL_SKELETON_PROPERTY_WIDTHS = ["w-24", "w-20", "w-28", "w-24", "w-32", "w-24", "w-24", "w-28", "w-32", "w-36"] as const;
function TaskDetailSkeletonPropertyRow(t0) {
  const $ = _c(6);
  const {
    valueWidth
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Skeleton className="size-3.5 shrink-0 rounded" />;
    t2 = <Skeleton className="ml-1.5 h-3 w-16" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== valueWidth) {
    t3 = cx("ml-auto h-6 rounded-full", valueWidth);
    $[2] = valueWidth;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <div className="-mx-1 flex min-h-[32px] items-center rounded px-1">{t1}{t2}<Skeleton className={t3} /></div>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function TaskDetailPanelSkeleton(t0) {
  const $ = _c(20);
  const {
    onClose,
    className,
    showCloseButton
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cx("flex h-full min-h-0 w-full flex-col", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="sr-only">Loading task details</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onClose || $[4] !== showCloseButton) {
    t3 = showCloseButton && <div className="flex items-center justify-end px-4 py-3"><Button variant="ghost" size="sm" className="aspect-square px-2" onClick={onClose} aria-label="Close task details"><RiCloseLine className="size-4" /></Button></div>;
    $[3] = onClose;
    $[4] = showCloseButton;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="space-y-2 pt-1"><Skeleton className="h-7 w-3/4" /><Skeleton className="h-3 w-48" /></div>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="space-y-2"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-11/12" /><Skeleton className="h-4 w-2/3" /></div>;
    t6 = <hr className="border-border" />;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="space-y-0.5">{TASK_DETAIL_SKELETON_PROPERTY_WIDTHS.map(_temp)}</div>;
    t8 = <hr className="border-border" />;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex items-center justify-between"><Skeleton className="h-3 w-16" /><Skeleton className="h-6 w-24 rounded-md" /></div>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Skeleton className="size-8 shrink-0 rounded-full" />;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex gap-3">{t10}<div className="min-w-0 flex-1 space-y-2"><div className="flex items-center gap-2"><Skeleton className="h-4 w-28" /><Skeleton className="h-3 w-16" /></div><Skeleton className="h-16 w-full rounded-lg" /></div></div>;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ScrollShadow.Root><ScrollShadow.Viewport className="space-y-4 px-4 pb-4">{t4}{t5}{t6}{t7}{t8}<div className="space-y-4">{t9}{t11}<div className="flex items-center gap-3 pl-2.5"><Skeleton className="size-3.5 rounded-full" /><Skeleton className="h-3 w-40" /><Skeleton className="h-3 w-14" /></div></div></ScrollShadow.Viewport><ScrollShadow.Top /><ScrollShadow.Bottom /></ScrollShadow.Root>;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Skeleton className="h-20 w-full rounded-lg" />;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="flex flex-col space-y-2 border-t border-border p-4">{t13}<div className="flex items-center justify-between"><Skeleton className="h-3 w-40" /><Skeleton className="h-8 w-16 rounded-md" /></div></div>;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== t1 || $[18] !== t3) {
    t15 = <div className={t1} aria-busy="true">{t2}{t3}{t12}{t14}</div>;
    $[17] = t1;
    $[18] = t3;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  return t15;
}
function _temp(width, index) {
  return <TaskDetailSkeletonPropertyRow key={`${width}-${index}`} valueWidth={width} />;
}
export function TaskDetailPanelContent({
  taskId,
  onClose,
  className,
  showCloseButton = true
}: TaskDetailPanelContentProps) {
  const {
    data: task,
    isLoading
  } = useTask(taskId);
  useTaskActivity(taskId);
  const {
    addComment,
    complete,
    create,
    delete: deleteTask,
    setSubscribers,
    subscribe,
    unsubscribe,
    update
  } = useTaskMutations();
  const canWriteTasks = useCanWriteTasks();
  const canAssignTasks = useCanAssignTasks();
  const {
    currentUser
  } = useUser();
  const {
    locations
  } = useLocations();
  const hasMultipleLocations = locations.length > 1;
  const [carePlanOpen, setCarePlanOpen] = useState(false);
  const {
    data: carePlanDetail
  } = useQuery({
    ...carePlanQueryOptions.detail(task?.carePlan?.carePlanId),
    enabled: carePlanOpen && !!task?.carePlan?.carePlanId
  });
  const bodyRef = useRef<HTMLDivElement>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [newComment, setNewComment] = useState("");
  const {
    selectedFiles: commentFiles,
    setSelectedFiles: setCommentFiles,
    addFiles: addCommentFiles,
    removeFile: removeCommentFile,
    isDragOver,
    dropZoneProps
  } = useComposerFiles();
  const [editorKey, setEditorKey] = useState(0);
  const [editingTitle, setEditingTitle] = useState(false);
  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setDescription(task.description || "");
    }
  }, [task]);

  // Title is a single field that must wrap to fill the panel rather than clip
  // on one line. Use an auto-growing textarea: reset to `auto` then size to
  // scrollHeight so it grows/shrinks with the wrapped content.
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const autosizeTitle = useCallback((el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  const titleRefCallback = useCallback((el_0: HTMLTextAreaElement | null) => {
    titleRef.current = el_0;
    autosizeTitle(el_0);
  }, [autosizeTitle]);
  useEffect(() => {
    autosizeTitle(titleRef.current);
  }, [title, autosizeTitle]);
  const commentCount = task?.comments?.length ?? 0;
  useEffect(() => {
    if (commentCount > 0) {
      const el_1 = bodyRef.current;
      if (el_1) el_1.scrollTop = el_1.scrollHeight;
    }
  }, [commentCount]);
  const handleUpdate = async (updates: TaskUpdate) => {
    try {
      await update.mutateAsync({
        taskId,
        task: updates
      });
    } catch (error) {
      toast({
        variant: "error",
        title: "Failed to update task",
        description: await parseError(error, "We couldn't update this task. Please try again.")
      });
    }
  };
  const handleComplete = async () => {
    if (!task || task.status === TaskStatus.COMPLETED || task.status === TaskStatus.CANCELLED) {
      return;
    }

    // No success toast: the Done button disappearing already confirms it, and
    // a toast would sit over the next task as the user moves on.
    try {
      await complete.mutateAsync(taskId);
    } catch (error_0) {
      toast({
        variant: "error",
        title: "Failed to complete task",
        description: await parseError(error_0, "We couldn't complete this task. Please try again.")
      });
    }
  };
  const handleBlurTitle = () => {
    if (title !== task?.title && title.trim()) {
      handleUpdate({
        title: title.trim()
      });
    } else if (!title.trim() && task?.title) {
      setTitle(task.title);
    }
    setEditingTitle(false);
  };
  const titleHasReferences = parseNotificationContent(title).some(s => s.type === "reference");
  const handleBlurDescription = () => {
    const newDesc = description.trim() || null;
    if (newDesc !== (task?.description || null)) {
      handleUpdate({
        description: newDesc
      });
    }
  };
  const isSubscribed = task?.subscribers?.some(sub => sub.providerId === currentUser?.id);
  const handleDelete = async () => {
    try {
      await deleteTask.mutateAsync(taskId);
      setShowDeleteConfirm(false);
      onClose();
    } catch (error_1) {
      toast({
        variant: "error",
        title: "Failed to delete task",
        description: await parseError(error_1, "We couldn't delete this task. Please try again.")
      });
    }
  };
  const handleDuplicate = async () => {
    if (!task) return;
    try {
      const newTask: TaskCreate = {
        title: `${task.title} (copy)`,
        description: task.description || undefined,
        priority: task.priority,
        dueDate: task.dueDate || undefined,
        patientId: task.patient?.id || undefined,
        assigneeId: task.assigneeId || undefined
      };
      await create.mutateAsync(newTask);
      toast({
        title: "Task duplicated"
      });
    } catch (error_2) {
      toast({
        variant: "error",
        title: "Failed to duplicate task",
        description: await parseError(error_2, "We couldn't duplicate this task. Please try again.")
      });
    }
  };
  const handleCommentSubmit = async () => {
    const content = newComment.trim();
    const files = commentFiles;
    if (!content && files.length === 0) return;
    setNewComment("");
    setCommentFiles([]);
    setEditorKey(k => k + 1);
    try {
      await addComment.mutateAsync({
        taskId,
        content,
        files
      });
    } catch (error_3) {
      setNewComment(content);
      setCommentFiles(files);
      setEditorKey(k_0 => k_0 + 1);
      if (files.length > 0) {
        toast({
          variant: "error",
          title: "Failed to add comment",
          description: await parseError(error_3, "We couldn't upload your files. Please try again.")
        });
      }
    }
  };
  const subscriberIds = task?.subscribers?.map(s_0 => s_0.providerId).filter((id): id is string => !!id) ?? [];
  const handleToggleSubscribe = () => {
    if (!currentUser?.id) return;
    // Use the dedicated per-user endpoints so team subscriptions on this task
    // (which aren't represented in `subscriberIds`) aren't wiped out by the
    // replace-all PUT /subscribers route.
    if (isSubscribed) {
      unsubscribe.mutate(taskId);
    } else {
      subscribe.mutate(taskId);
    }
  };
  if (isLoading || !task) {
    return <TaskDetailPanelSkeleton onClose={onClose} className={className} showCloseButton={showCloseButton} />;
  }
  const canCompleteTask = canWriteTasks && task.status !== TaskStatus.COMPLETED && task.status !== TaskStatus.CANCELLED;
  return <>
      <div className={cx("flex h-full min-h-0 w-full flex-col", className)}>
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <div className="min-w-0 flex-1">
            {canCompleteTask && <Button variant="secondary" size="sm" onClick={handleComplete} isLoading={complete.isPending} loadingText="Marking done" icon={RiCheckLine} className="h-8 border-status-success-text/30 bg-status-success-bg text-status-success-text hover:bg-status-success-bg/80">
                Mark done
              </Button>}
          </div>
          <div className="flex items-center gap-2">
            {canWriteTasks && <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="px-2">
                    <RiMore2Fill className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleDuplicate}>
                    <RiFileCopyLine className="size-4 mr-2" />
                    Duplicate task
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowDeleteConfirm(true)} className="text-status-error-text focus:text-status-error-text">
                    <RiDeleteBinLine className="size-4 mr-2" />
                    Delete task
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>}
            {showCloseButton && <Button variant="ghost" size="sm" className="aspect-square px-2" onClick={onClose} aria-label="Close task details">
                <RiCloseLine className="size-4" />
              </Button>}
          </div>
        </div>

        <ScrollShadow.Root>
          <ScrollShadow.Viewport ref={bodyRef} className="space-y-4 px-4 pb-4">
            {/* ── Title ── */}
            <div>
              {titleHasReferences && !editingTitle ? <div role="button" tabIndex={0} onClick={() => canWriteTasks && setEditingTitle(true)} onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setEditingTitle(true);
              }
            }} className="cursor-text rounded text-h3 leading-tight text-foreground hover:bg-hover">
                  <NotificationReferenceContent content={title} taskId={taskId} newWindow={false} />
                </div> : <textarea ref={titleRefCallback} value={title} rows={1} readOnly={!canWriteTasks} onChange={e_0 => {
              setTitle(e_0.target.value);
              autosizeTitle(e_0.target);
            }} onBlur={handleBlurTitle} onKeyDown={e_1 => {
              // Titles are single-line semantically; Enter commits instead
              // of inserting a newline (it still soft-wraps as it fills).
              if (e_1.key === "Enter") {
                e_1.preventDefault();
                e_1.currentTarget.blur();
              }
            }} placeholder="Task title" className={cx("block w-full resize-none overflow-hidden break-words", "border-0 bg-transparent p-0 text-h3 leading-tight shadow-none", "!ring-0 !outline-none text-foreground placeholder:text-foreground-muted")} />}
              <p className="mt-1 text-caption text-foreground-muted">
                <span>
                  {task.creator ? `Created by ${formatStaffName(task.creator)} · ` : ""}
                </span>
                {calculateRelativeDuration(task.createdDate)}
                <span>
                  {task.completedDate && ` · Completed ${calculateRelativeDuration(task.completedDate)}`}
                </span>
              </p>
            </div>

            {/* ── Description ──
              Always render the editor (never a read-only swap): it renders
              `{{type:display:id}}` references as inline chips, so it stays
              formatted while remaining directly editable. The old read-only ↔
              editor toggle dropped the caret on click and is what made
              reference descriptions feel uneditable. */}
            <div onBlur={handleBlurDescription} tabIndex={-1}>
              <TaskMentionEditor value={description} onChange={setDescription} editable={canWriteTasks} placeholder="Add description..." className="!border-0 !bg-transparent !shadow-none !ring-0 !p-0 min-h-[32px] text-body text-foreground-secondary" />
            </div>

            <hr className="border-border" />

            {/* ── Properties ── */}
            {/* A disabled fieldset disables every control inside it, which is
                what the inline editors below are. The resets make each one lay
                out exactly like the div it replaces — a fieldset otherwise
                brings its own border, padding and min-inline-size. Three
                siblings rather than one, because assignee and team are gated on
                the narrower tasks:assign; nesting them would not help, since a
                disabled outer fieldset disables everything under it. */}
            <div className="min-w-0 space-y-0.5">
              <fieldset disabled={!canWriteTasks} className="m-0 min-w-0 border-0 p-0 space-y-0.5">
                <PropertyRow icon={RiRadioButtonLine} label="Status">
                  <StatusBadge value={task.status} onChange={s_1 => handleUpdate({
                  status: s_1
                })} />
                </PropertyRow>

                <PropertyRow icon={RiFlagLine} label="Priority">
                  <PriorityBadge value={task.priority} onChange={p => handleUpdate({
                  priority: p
                })} />
                </PropertyRow>
              </fieldset>

              <fieldset disabled={!canAssignTasks} className="m-0 min-w-0 border-0 p-0 space-y-0.5">
                <PropertyRow icon={RiUser3Line} label="Assignee">
                  <AssigneeBadge value={task.assigneeId || undefined} onChange={a => handleUpdate({
                  assigneeId: a || null,
                  ...(a ? {
                    teamId: null
                  } : {})
                })} />
                </PropertyRow>

                <PropertyRow icon={RiTeamLine} label="Team">
                  <TeamBadge value={task.teamId || undefined} onChange={t => handleUpdate({
                  teamId: t || null,
                  ...(t ? {
                    assigneeId: null
                  } : {})
                })} />
                </PropertyRow>
              </fieldset>

              <fieldset disabled={!canWriteTasks} className="m-0 min-w-0 border-0 p-0 space-y-0.5">
                <PropertyRow icon={RiUserHeartLine} label="Patient">
                  <PatientBadge value={task.patient as PatientSummary | undefined} onChange={p_0 => handleUpdate({
                  patientId: p_0?.id || null
                })} />
                </PropertyRow>

                {!!task.opportunityId && <PropertyRow icon={RiExternalLinkLine} label="Lead">
                    <div className="px-2">
                      <ProgressBarLink href={`/growth/leads?view=kanban&leadId=${encodeURIComponent(task.opportunityId)}`} className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-caption text-foreground hover:bg-hover">
                        <span>Open lead</span>
                        <RiExternalLinkLine className="size-3" />
                      </ProgressBarLink>
                    </div>
                  </PropertyRow>}

                {hasMultipleLocations && <PropertyRow icon={RiMapPinLine} label="Location">
                    <LocationBadge value={task.locationId || undefined} onChange={l => handleUpdate({
                  locationId: l || null
                })} />
                  </PropertyRow>}

                <PropertyRow icon={RiCalendarLine} label="Due Date">
                  <SmartDateInput placeholder="Set date..." value={task.dueDate ? dayMarker(task.dueDate) : undefined} onChange={d => handleUpdate({
                  dueDate: d ? pickedDay(d) : null
                })} className="!border-0 !bg-transparent !shadow-none !ring-0 !text-caption !h-7 !py-0 !px-2" />
                </PropertyRow>

                <PropertyRow icon={RiTimeLine} label="Start Date">
                  <SmartDateInput placeholder="Set date..." value={task.startDate ? dayMarker(task.startDate) : undefined} onChange={d_0 => handleUpdate({
                  startDate: d_0 ? pickedDay(d_0) : null
                })} className="!border-0 !bg-transparent !shadow-none !ring-0 !text-caption !h-7 !py-0 !px-2" />
                </PropertyRow>

                <PropertyRow icon={RiUserLine} label="Creator">
                  <span className="px-2 text-caption text-foreground">
                    {task.creator ? formatStaffName(task.creator) : "-"}
                  </span>
                </PropertyRow>

                <PropertyRow icon={RiCalendarCheckLine} label="Created">
                  <span className="px-2 text-caption text-foreground">
                    {formatDateTime(task.createdDate)}
                  </span>
                </PropertyRow>

                {!!task.completedDate && <PropertyRow icon={RiTimeLine} label="Completed">
                    <span className="px-2 text-caption text-foreground">
                      {formatDateTime(task.completedDate)}
                    </span>
                  </PropertyRow>}

                {!!task.carePlan && <PropertyRow icon={RiHeartPulseLine} label="Care Plan">
                    <Popover open={carePlanOpen} onOpenChange={setCarePlanOpen}>
                      <PopoverTrigger asChild>
                        <button type="button" className="cursor-pointer">
                          <CarePlanBadge carePlan={task.carePlan} />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent side="bottom" align="start" className="w-[480px] max-h-[400px] overflow-y-auto p-0">
                        {carePlanDetail && (task.patient?.id || task.patientId) ? <ReadOnlyCarePlan carePlan={carePlanDetail} patientId={(task.patient?.id ?? task.patientId) as string} /> : <div className="flex items-center justify-center py-8">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-foreground-muted border-t-transparent" />
                          </div>}
                      </PopoverContent>
                    </Popover>
                  </PropertyRow>}

                {!!task.linkedCalls?.length && <PropertyRow icon={RiPhoneLine} label={task.linkedCalls.length > 1 ? "Calls" : "Call"}>
                    <div className="flex flex-wrap gap-1 px-2">
                      {task.linkedCalls.map(call => <ProgressBarLink key={call.id} href={`/comms/call?callId=${encodeURIComponent(call.id)}&taskId=${encodeURIComponent(taskId)}`} className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-caption text-foreground hover:bg-hover">
                          <span>
                            {call.createdDate ? formatDateTime(call.createdDate) : call.id}
                          </span>
                          <RiExternalLinkLine className="size-3" />
                        </ProgressBarLink>)}
                    </div>
                  </PropertyRow>}

                {!!task.targetFaxId && <PropertyRow icon={RiPrinterLine} label="Fax">
                    <div className="px-2">
                      <ProgressBarLink href={`/comms/fax?faxId=${encodeURIComponent(task.targetFaxId)}`} className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-caption text-foreground hover:bg-hover">
                        <span>View fax</span>
                        <RiExternalLinkLine className="size-3" />
                      </ProgressBarLink>
                    </div>
                  </PropertyRow>}

                {!!task.targetDocumentId && !!task.patientId && <PropertyRow icon={RiFileList2Line} label="Document">
                    <div className="px-2">
                      <ProgressBarLink href={`/patient/${encodeURIComponent(task.patientId)}?tab=forms-documents`} className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-caption text-foreground hover:bg-hover">
                        <span>View document</span>
                        <RiExternalLinkLine className="size-3" />
                      </ProgressBarLink>
                    </div>
                  </PropertyRow>}

                <PropertyRow icon={RiNotificationLine} label="Subscribers">
                  <SubscribersBadge subscriberIds={subscriberIds} assigneeId={task.assigneeId || undefined} onChange={providerIds => setSubscribers.mutate({
                  taskId,
                  providerIds
                })} />
                </PropertyRow>
              </fieldset>
            </div>

            <hr className="border-border" />

            {/* ── Activity ── */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-caption font-medium text-foreground-muted uppercase tracking-wide">
                  Activity
                </h3>
                <Button variant="ghost" size="sm" onClick={handleToggleSubscribe} disabled={!canWriteTasks} className="text-foreground-muted hover:text-foreground h-6 text-caption">
                  {isSubscribed ? <>
                      <RiNotificationOffLine className="size-3 mr-1" />
                      Unsubscribe
                    </> : <>
                      <RiNotificationLine className="size-3 mr-1" />
                      Subscribe
                    </>}
                </Button>
              </div>

              <TaskActivityTimeline taskId={taskId} comments={task.comments || []} />
            </div>
          </ScrollShadow.Viewport>
          <ScrollShadow.Top />
          <ScrollShadow.Bottom />
        </ScrollShadow.Root>

        {canWriteTasks && <div className={cx("flex flex-col space-y-1 border-t border-border p-4", isDragOver && "bg-primary/5")} {...dropZoneProps}>
            <ComposerFilePreviews files={commentFiles} onRemove={removeCommentFile} />
            <TaskMentionEditor key={editorKey} placeholder="Add a comment..." value={newComment} onChange={setNewComment} onEnter={handleCommentSubmit} />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ComposerAttachButton onFiles={addCommentFiles} />
                <span className="text-tiny text-foreground-muted">
                  Enter to submit, Shift+Enter for new line
                </span>
              </div>
              <Button size="sm" variant="primary" onClick={handleCommentSubmit} disabled={!newComment.trim() && commentFiles.length === 0} icon={RiSendPlane2Line}>
                Send
              </Button>
            </div>
          </div>}
      </div>

      <DeleteConfirmationModal isOpen={showDeleteConfirm} setIsOpen={setShowDeleteConfirm} onConfirm={handleDelete} title="Delete Task" subtitle="Are you sure you want to delete this task?" confirmText="Delete" isLoading={deleteTask.isPending} />
    </>;
}
export function TaskDetailModal(t0) {
  const $ = _c(10);
  const {
    taskId,
    isOpen,
    onClose
  } = t0;
  let t1;
  if ($[0] !== onClose) {
    t1 = open => !open && onClose();
    $[0] = onClose;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DrawerTitle className="sr-only">Task Details</DrawerTitle>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onClose || $[4] !== taskId) {
    t3 = <DrawerContent className="md:max-w-xl p-0" onInteractOutside={preventTaskDetailInteractOutside}>{t2}<TaskDetailPanelContent taskId={taskId} onClose={onClose} /></DrawerContent>;
    $[3] = onClose;
    $[4] = taskId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isOpen || $[7] !== t1 || $[8] !== t3) {
    t4 = <Drawer open={isOpen} onOpenChange={t1} modal={false}>{t3}</Drawer>;
    $[6] = isOpen;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}